// src/user/Apps.js
import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { portalGradientTextSx } from '../styles/dashboardBrand';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Grid,
  Divider,
  Container,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  PhoneAndroid,
  Apple,
  OpenInNew,
  VideoLibrary,
  SportsEsports,
  MoreHoriz,
  InfoOutlined,
  MonetizationOn,
} from '@mui/icons-material';
import dreameyVidsIcon from '../Icons/Dreamey Vids Icon.png';
import ludoIcon from '../Icons/Ludo Icon.png';
import dreameyCardsIcon from '../Icons/DreameyCards Icon.png';
import dreameyRunIcon from '../Icons/Dreamey Run Icon.png';
import dreameySnookerIcon from '../Icons/Dreamey Snooker Icon.png';
import dreamey2048Icon from '../Icons/Dreamey 2048 Icon.png';
import dreameyBlocksIcon from '../Icons/Dreamey Blocks.png';

const APP_STORE_DEVELOPER_URL = 'https://apps.apple.com/us/developer/imaginative-solutions/id1855143912';
const PLAY_STORE_DEVELOPER_URL = 'https://play.google.com/store/apps/developer?id=Imaginative+Solutions&hl=en';

function Apps() {
  const [supportInfoOpen, setSupportInfoOpen] = useState(false);
  const [coinsByAppName, setCoinsByAppName] = useState({});

  useEffect(() => {
    api.get('/app-coins/apps')
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.apps)) {
          const map = {};
          res.data.apps.forEach((a) => {
            map[a.appName] = a.dreameyDiamonds ?? 0;
          });
          setCoinsByAppName(map);
        }
      })
      .catch(() => {});
  }, []);

  const isAppleDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || navigator.vendor || '';
    return /iPad|iPhone|iPod|Macintosh|Mac OS/i.test(ua);
  };

  const handleCardClick = () => {
    if (isAppleDevice()) {
      window.open(APP_STORE_DEVELOPER_URL, '_blank', 'noopener,noreferrer');
    } else {
      window.open(PLAY_STORE_DEVELOPER_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // Main App - Dreamey Vids
  const dreameyVidsAndroid = {
    id: 'dreamey-vids-android',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: dreameyVidsIcon,
    iconType: 'image',
    url: 'https://play.google.com/store/apps/details?id=com.dreamey.app&pcampaignid=web_share',
    color: '#4FACFE',
    platform: 'android',
  };

  const dreameyVidsIOS = {
    id: 'dreamey-vids-ios',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: dreameyVidsIcon,
    iconType: 'image',
    url: 'https://apps.apple.com/pk/app/dreamey-vids/id6755639217',
    color: '#4FACFE',
    platform: 'ios',
  };

  // Other Apps - Dreamey Ludo
  const dreameyLudoAndroid = {
    id: 'dreamey-ludo-android',
    name: 'Dreamey Ludo',
    description: 'Classic Ludo board game with multiple game modes and beautiful design',
    icon: ludoIcon,
    iconType: 'image',
    url: 'https://play.google.com/store/apps/details?id=com.dreamey.ludo&pcampaignid=web_share',
    color: '#f5576c',
    platform: 'android',
  };

  const dreameyLudoIOS = {
    id: 'dreamey-ludo-ios',
    name: 'Dreamey Ludo',
    description: 'Classic Ludo board game with multiple game modes and beautiful design',
    icon: ludoIcon,
    iconType: 'image',
    url: 'https://apps.apple.com/pk/app/dreamey-ludo/id6756227591',
    color: '#f5576c',
    platform: 'ios',
  };

  // Coming Soon - Dreamey Cards (same layout as Ludo: Android/iOS chips, two-line description)
  const dreameyCardsAndroid = {
    id: 'dreamey-cards-android',
    name: 'Dreamey Cards S',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyCardsIcon,
    iconType: 'image',
    url: null,
    color: '#10b981',
    platform: 'android',
    comingSoon: true,
  };

  const dreameyCardsIOS = {
    id: 'dreamey-cards-ios',
    name: 'Dreamey Cards S',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyCardsIcon,
    iconType: 'image',
    url: null,
    color: '#10b981',
    platform: 'ios',
    comingSoon: true,
  };

  // Coming Soon - Dreamey Run (same layout as Ludo: Android/iOS chips, two-line description)
  const dreameyRunAndroid = {
    id: 'dreamey-run-android',
    name: 'Dreamey Run J',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyRunIcon,
    iconType: 'image',
    url: null,
    color: '#f59e0b',
    platform: 'android',
    comingSoon: true,
  };

  const dreameyRunIOS = {
    id: 'dreamey-run-ios',
    name: 'Dreamey Run J',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyRunIcon,
    iconType: 'image',
    url: null,
    color: '#f59e0b',
    platform: 'ios',
    comingSoon: true,
  };

  // Coming Soon - Dreamey Snooker J
  const dreameySnookerAndroid = {
    id: 'dreamey-snooker-android',
    name: 'Dreamey Snooker J',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameySnookerIcon,
    iconType: 'image',
    url: null,
    color: '#0ea5e9',
    platform: 'android',
    comingSoon: true,
  };
  const dreameySnookerIOS = {
    id: 'dreamey-snooker-ios',
    name: 'Dreamey Snooker J',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameySnookerIcon,
    iconType: 'image',
    url: null,
    color: '#0ea5e9',
    platform: 'ios',
    comingSoon: true,
  };

  // Coming Soon - Dreamey 2048 A
  const dreamey2048Android = {
    id: 'dreamey-2048-android',
    name: 'Dreamey 2048 A',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreamey2048Icon,
    iconType: 'image',
    url: null,
    color: '#f97316',
    platform: 'android',
    comingSoon: true,
  };
  const dreamey2048IOS = {
    id: 'dreamey-2048-ios',
    name: 'Dreamey 2048 A',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreamey2048Icon,
    iconType: 'image',
    url: null,
    color: '#f97316',
    platform: 'ios',
    comingSoon: true,
  };

  // Coming Soon - Dreamey Blocks K
  const dreameyBlocksAndroid = {
    id: 'dreamey-blocks-android',
    name: 'Dreamey Blocks K',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyBlocksIcon,
    iconType: 'image',
    url: null,
    color: '#8b5cf6',
    platform: 'android',
    comingSoon: true,
  };
  const dreameyBlocksIOS = {
    id: 'dreamey-blocks-ios',
    name: 'Dreamey Blocks K',
    description: "Coming soon. We're working on bringing this app to Android and iOS.",
    icon: dreameyBlocksIcon,
    iconType: 'image',
    url: null,
    color: '#8b5cf6',
    platform: 'ios',
    comingSoon: true,
  };

  const handleAppClick = (url) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const AppCard = ({ app, platform }) => (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: app.comingSoon ? 'default' : 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          borderColor: app.color || 'primary.main',
        },
      }}
      onClick={() => !app.comingSoon && handleAppClick(app.url)}
    >
      <CardContent sx={{ px: { xs: '12px', sm: '16px', md: '24px' }, py: { xs: '8px', sm: '12px', md: '16px' }, '&:last-child': { pb: { xs: '8px', sm: '12px', md: '16px' } } }}>
        <Box>
          {/* First Row: App Name */}
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.5rem' },
              lineHeight: 1.2,
              textAlign: 'center',
              mb: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {app.name}
          </Typography>

          {/* Second Row: App Icon, Platform Tag, and Link Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
            {/* App Icon */}
            <Box
              sx={{
                width: { xs: 56, sm: 48, md: 64 },
                height: { xs: 56, sm: 48, md: 64 },
                borderRadius: 2,
                bgcolor: (app.iconType === 'image' && app.icon) ? 'transparent' : (app.color || 'primary.main'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {app.iconType === 'image' && app.icon ? (
                <img
                  src={app.icon}
                  alt={`${app.name} icon`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <MoreHoriz sx={{ fontSize: { xs: 32, sm: 28, md: 36 } }} />
              )}
            </Box>

            {/* Platform Tag: "Available on" text + Android/iOS mini chips, or "Coming Soon" */}
            {app.comingSoon ? (
              <Chip
                label="Coming Soon"
                size="small"
                sx={{
                  height: { xs: 20, sm: 24, md: 28 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                  bgcolor: '#f97316',
                  color: 'white',
                  '&:hover': { bgcolor: '#ea580c' },
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, fontWeight: 600, color: '#22c55e' }}>
                  Available on
                </Typography>
                <Chip
                  label="Android"
                  size="small"
                  icon={<PhoneAndroid sx={{ fontSize: 'inherit' }} />}
                  sx={{ height: { xs: 20, sm: 24, md: 28 }, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, '& .MuiChip-icon': { fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } } }}
                />
                <Chip
                  label="iOS"
                  size="small"
                  icon={<Apple sx={{ fontSize: 'inherit' }} />}
                  sx={{ height: { xs: 20, sm: 24, md: 28 }, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }, '& .MuiChip-icon': { fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' } } }}
                />
              </Box>
            )}

            {/* Link Button - Hidden */}
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              sx={{
                display: 'none',
                color: 'text.secondary',
                padding: { xs: '4px', sm: '8px' },
                '&:hover': {
                  color: app.color || 'primary.main',
                },
                '& svg': {
                  fontSize: { xs: '16px', sm: '18px', md: '24px' },
                },
              }}
            >
              <OpenInNew />
            </IconButton>
          </Box>

          {/* Description - Hidden on mobile, shown on desktop */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              lineHeight: 1.6,
              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
              display: { xs: 'none', sm: 'block' },
              mt: { xs: 0, sm: 1.5, md: 2 },
            }}
          >
            {app.description}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  // Combined card: one card per app with name on top center, icon left, Android + iOS tabs
  // All links go to developer pages: App Store (iOS/Mac) or Play Store (Android/Web)
  const AppCardCombined = ({ androidApp, iosApp, matchSizeOnDesktop, dreameyDiamonds }) => {
    const app = androidApp; // name, icon, description same for both
    const isComingSoon = app.comingSoon;
    const coins = dreameyDiamonds ?? 0;
    return (
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid',
          borderColor: 'divider',
          cursor: isComingSoon ? 'default' : 'pointer',
          height: 100,
          ...(matchSizeOnDesktop && {
            width: { xs: 'auto', md: '100%' },
            minWidth: { md: 0 },
          }),
          '&:hover': {
            boxShadow: 6,
            borderColor: app.color || 'primary.main',
          },
        }}
        onClick={!isComingSoon ? handleCardClick : undefined}
      >
        <CardContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 2, px: '12px', py: '10px', height: '100%', boxSizing: 'border-box', '&:last-child': { pb: '10px' } }}>
          {/* Left: Icon - full height of card, same size for all cards */}
          <Box
            sx={{
              width: 72,
              flexShrink: 0,
              alignSelf: 'stretch',
              borderRadius: 2,
              bgcolor: (app.iconType === 'image' && app.icon) ? 'transparent' : (app.color || 'primary.main'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {app.iconType === 'image' && app.icon ? (
              <img src={app.icon} alt={`${app.name} icon`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <MoreHoriz sx={{ fontSize: 36 }} />
            )}
          </Box>
          {/* Right: App name + Android/iOS + description */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                fontSize: '1.05rem',
                lineHeight: 1.2,
                textAlign: 'left',
              }}
            >
              {app.name}
            </Typography>
            {!isComingSoon && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MonetizationOn
                  sx={{
                    fontSize: '1.1rem',
                    color: '#c9a227',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#c9a227',
                    fontSize: '0.8rem',
                  }}
                >
                  {coins.toLocaleString()} Dreamey Coins
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {isComingSoon ? (
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{
                    height: 28,
                    fontSize: '0.75rem',
                    bgcolor: '#f97316',
                    color: 'white',
                    '&:hover': { bgcolor: '#ea580c' },
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#22c55e' }}>
                    Available on
                  </Typography>
                  <Chip
                    label="Android"
                    size="small"
                    icon={<PhoneAndroid sx={{ fontSize: 'inherit' }} />}
                    sx={{
                      height: 28,
                      fontSize: '0.65rem',
                      pointerEvents: 'none',
                      '& .MuiChip-icon': { fontSize: '0.9rem' },
                    }}
                  />
                  <Chip
                    label="iOS"
                    size="small"
                    icon={<Apple sx={{ fontSize: 'inherit' }} />}
                    sx={{
                      height: 28,
                      fontSize: '0.65rem',
                      pointerEvents: 'none',
                      '& .MuiChip-icon': { fontSize: '0.9rem' },
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                fontSize: '0.65rem',
                display: 'none',
              }}
            >
              {app.description}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const PlatformSection = ({ platform, apps, title, icon, gradient }) => (
    <Box>
      {/* Platform Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          mb: { xs: 2, sm: 2.5, md: 3 },
          p: { xs: 1, sm: 1.5, md: 2 },
          borderRadius: 2,
          background: gradient,
          color: 'white',
        }}
      >
        <Box
          sx={{
            width: { xs: 40, sm: 48, md: 56 },
            height: { xs: 40, sm: 48, md: 56 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 0.5,
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.5rem' },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
            }}
          >
            {apps.length} {apps.length === 1 ? 'app available' : 'apps available'}
          </Typography>
        </Box>
      </Box>

      {/* Apps Grid */}
      {apps.length > 0 ? (
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
          {apps.map((app) => (
            <Grid item xs={12} key={app.id}>
              <AppCard app={app} platform={platform} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '2px dashed',
            borderColor: 'divider',
          }}
        >
          <MoreHoriz sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
            More apps coming soon
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We're working on bringing our apps to {platform === 'android' ? 'Android' : 'iOS'}.
            Stay tuned for updates!
          </Typography>
        </Paper>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Dreamey Vids Section - before Please Support Us */}
      <Box sx={{ mb: { xs: 2, sm: 6 } }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={portalGradientTextSx({
            fontWeight: 700,
            mb: { xs: 1.5, sm: 3 },
            fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
            textAlign: 'center',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
            '@keyframes popInOut': {
              '0%, 200%': { transform: 'scale(1)', opacity: 1.1 },
              '50%': { transform: 'scale(1.08)', opacity: 0.8 },
            },
            animation: 'popInOut 1.5s ease-in-out infinite',
          })}
        >
          Our Project
        </Typography>
        <Box sx={{ maxWidth: 380, mx: 'auto' }}>
          <AppCardCombined androidApp={dreameyVidsAndroid} iosApp={dreameyVidsIOS} dreameyDiamonds={coinsByAppName['Dreamey Vids']} />
        </Box>
      </Box>

      {/* Please Support Us */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: { xs: 1.5, sm: 3 } }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={portalGradientTextSx({
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
              textAlign: 'center',
              mb: 0,
            })}
          >
            Please Support Our Other Apps
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSupportInfoOpen(true)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            aria-label="Information about these apps"
          >
            <InfoOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>
        <Dialog open={supportInfoOpen} onClose={() => setSupportInfoOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>About these apps</DialogTitle>
          <DialogContent>
            <DialogContentText>
              These apps are not part of the engagement program. Downloading them helps support our work. All apps are free to download and use.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSupportInfoOpen(false)} color="primary" autoFocus>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
        {/* One card per app: single column on mobile; on desktop 3 per row, each card same size as Dreamey Vids (380×100) */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: 'unset' },
            gap: 2,
            maxWidth: { xs: 380, md: 1200 },
            mx: 'auto',
            gridTemplateColumns: { md: 'repeat(3, 380px)' },
            justifyContent: { md: 'center' },
          }}
        >
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreameyLudoAndroid} iosApp={dreameyLudoIOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey Ludo']} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreameySnookerAndroid} iosApp={dreameySnookerIOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey Snooker J']} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreamey2048Android} iosApp={dreamey2048IOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey 2048 A']} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreameyCardsAndroid} iosApp={dreameyCardsIOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey Cards S']} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreameyBlocksAndroid} iosApp={dreameyBlocksIOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey Blocks K']} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: 380 }, minWidth: { md: 0 } }}>
            <AppCardCombined androidApp={dreameyRunAndroid} iosApp={dreameyRunIOS} matchSizeOnDesktop dreameyDiamonds={coinsByAppName['Dreamey Run J']} />
          </Box>
        </Box>
      </Box>

      {/* Additional Info Section */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            If you encounter any issues downloading or installing our apps, please contact our support team.
            All apps are free to download and use.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
}

export default Apps;
